import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";

export default function AdminLogin() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");

  useEffect(() => {
    axios.get(`${API}/admin/setup/status`).then((r) => {
      if (!r.data.initialized) nav("/admin/setup");
    });
  }, [nav]);

  const submit = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/login`, { email: email.trim().toLowerCase(), password: pwd });
      nav("/admin");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Sign in failed");
    } finally {
      setBusy(false);
    }
  };

  const requestReset = async () => {
    if (!forgotEmail.trim()) return toast.error("Please enter your admin email");
    setBusy(true);
    try {
      await axios.post(`${API}/admin/password-reset/request`, { email: forgotEmail.trim().toLowerCase() });
      toast.success("If the address is registered, a reset link has been sent.");
      setForgot(false); setForgotEmail("");
    } catch {
      toast.error("Could not process request");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-6">
      <div className="w-full max-w-md" data-testid="admin-login">
        <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-3">KT Milano · Admin</div>
        <h1 className="font-serif text-3xl text-[#383633] tracking-tight mb-6">
          {forgot ? "Reset password" : "Sign in"}
        </h1>
        <div className="space-y-4 bg-white border kt-line rounded-2xl p-6 kt-shadow">
          {!forgot ? (
            <>
              <div>
                <Label className="text-xs kt-tracking uppercase text-[#6F6B66]">Email</Label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2 h-11 border-[#ECE7DF]" data-testid="login-email" />
              </div>
              <div>
                <Label className="text-xs kt-tracking uppercase text-[#6F6B66]">Password</Label>
                <Input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} className="mt-2 h-11 border-[#ECE7DF]" data-testid="login-password" />
              </div>
              <Button onClick={submit} disabled={busy || !email || !pwd} data-testid="login-submit"
                className="w-full rounded-full h-12 bg-[#383633] hover:bg-[#4A4238] text-white text-xs kt-tracking uppercase">
                {busy ? "Signing in…" : "Sign in"}
              </Button>
              <button type="button" onClick={() => setForgot(true)} data-testid="forgot-link"
                className="text-[11px] text-[#6F6B66] hover:text-[#383633] underline underline-offset-2">
                Forgot password?
              </button>
            </>
          ) : (
            <>
              <p className="text-sm text-[#6F6B66] leading-relaxed">
                Enter the Owner/Admin email. If it matches an account, a single-use link
                (valid for 60 minutes) will be sent to that inbox.
              </p>
              <div>
                <Label className="text-xs kt-tracking uppercase text-[#6F6B66]">Email</Label>
                <Input type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)}
                  className="mt-2 h-11 border-[#ECE7DF]" data-testid="forgot-email" />
              </div>
              <Button onClick={requestReset} disabled={busy || !forgotEmail} data-testid="forgot-submit"
                className="w-full rounded-full h-12 bg-[#D8B589] hover:bg-[#C79E6E] text-[#3A2C1B] text-xs kt-tracking uppercase">
                {busy ? "Sending…" : "Send reset link"}
              </Button>
              <button type="button" onClick={() => setForgot(false)}
                className="text-[11px] text-[#6F6B66] hover:text-[#383633] underline underline-offset-2">
                ← Back to sign in
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
